import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { Accordion, AccordionDetails, AccordionSummary, Grid } from "@mui/material"
import { Button, InputText, NotificationUtils, Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { v4 as uuidv4 } from "uuid"

import { MonacoTextEditor } from "../../../components/data-display/MonacoTextEditor"
import { getTheme } from "../../../hooks/UseTheme"
import { useResizeObserver } from "../../../hooks/useResizeObserver"
import { KonanAPI } from "../../../services/KonanAPI"
import { ComponentTest } from "../../../types/generated/api/ComponentTest"
import type { DeploymentSchemaFeatures } from "../../../types/generated/api/DeploymentSchemaFeatures"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import { convertNestedObjectsToFeatureStrings } from "../../../utils/modelDetailsHelpers"
import {
  type EditorType,
  type Schema,
  type SchemaFeature,
  type SelectedComponent,
  type TestMutationParams,
  type TestingOutputProps,
} from "../Interfaces"
import { EDITOR_TYPES } from "../Interfaces"
import {
  convertJsonToTableSchema,
  convertNestedFeatureStringsToObjects,
  dummyValueBasedOnType,
  getApiComponentType,
  getComponentDataFromType,
  typeCastBasedOnType,
} from "../helpers"
import { TabularEditor } from "./editors/TabularEditor"

import styles from "./ComponentTestCreation.module.scss"

/**
 * Component to display testing results in a formatted JSON viewer
 * @param data - The testing result data to display
 * @returns {ReactElement} A formatted display of the testing output
 */

const TestingOutput = ({ data, isExpanded, setIsExpanded }: TestingOutputProps): ReactElement => {
  const { t } = useTranslation("predictions", {
    keyPrefix: "componentTesting.creation",
  })

  const handleChange = (event: React.SyntheticEvent, expanded: boolean): void => {
    setIsExpanded(expanded)
  }

  return (
    <Accordion expanded={isExpanded} onChange={handleChange} className={styles.accordion}>
      <AccordionSummary
        expandIcon={
          <KeyboardArrowDownIcon
            style={{
              color: "var(--secondary-icon-default)",
            }}
          />
        }
        className={styles.accordionSummary}
      >
        <Typography variant="label-bold">{t("componentOutput")}</Typography>
      </AccordionSummary>

      <AccordionDetails className={styles.accordionDetails}>
        <MonacoTextEditor
          value={
            data.status === ComponentTest.status.FAILED
              ? JSON.stringify(
                  {
                    [t("jsonEditor.status")]: data.status,
                  },
                  null,
                  2,
                )
              : JSON.stringify({ [t("jsonEditor.output")]: data.output }, null, 2)
          }
          readOnly
          language="json"
          automaticLayout
          borderRadius="0 0 8px 8px"
          height="100%"
        />
      </AccordionDetails>
    </Accordion>
  )
}

/**
 * ComponentTestingCreation component allows users to create testing for a single component
 * using either a table-based form or JSON editor interface.
 *
 * Features:
 * - Dual editor modes (table/JSON)
 * - Dynamic schema validation
 * - Real-time JSON validation
 * - Pagination for table view
 * - Search functionality
 * - Support for multiple data types
 *
 * @returns {ReactElement} A form interface for creating testing
 */

interface ComponentTestingProps {
  fromComponentPage?: boolean
  selectedComponent: SelectedComponent
}

export function ComponentTestingCreation({ selectedComponent }: ComponentTestingProps): ReactElement {
  const { t } = useTranslation("predictions", {
    keyPrefix: "componentTesting.creation",
  })

  const { id: projectId } = useParams<{ id: string }>()
  const theme = getTheme()

  // Track current editor mode (table or JSON)
  const [editorType, setEditorType] = useState<EditorType>(EDITOR_TYPES.TABLE)

  // Store JSON string representation of the data
  const [json, setJson] = useState<string | undefined>("{}")

  const [localSchema, setLocalSchema] = useState<Schema>({})

  // Pagination and search state for table view
  const [page, setPage] = useState<number>(1)
  const [search, setSearch] = useState<string | undefined>(undefined)

  // Track JSON validity for error handling
  const [isJsonValid, setIsJsonValid] = useState<boolean>(true)

  const [isOutputExpanded, setIsOutputExpanded] = useState<boolean>(false)

  const [tableRef, { height }] = useResizeObserver()

  const rowsPerPage = Math.floor(((height ?? 0) - 37 - 5) / 41)

  const { data: schemaData, isLoading: isSchemaLoading } = useQuery({
    queryKey: ["componentSchema", projectId, selectedComponent.data.uuid, selectedComponent.type],
    queryFn: () => {
      return KonanAPI.getComponentSchema({
        projectId: projectId as string,
        componentId: selectedComponent.data.uuid as string,
        componentType: getApiComponentType(selectedComponent.type),
      })
    },
    enabled: !!projectId && !!selectedComponent.data.uuid && !!selectedComponent.type,
    onSuccess: (data) => {
      if (data?.data) {
        const initialSchema = data.data.reduce(
          (acc: Record<string, SchemaFeature>, feature: DeploymentSchemaFeatures) => ({
            ...acc,
            [feature.name]: {
              ...feature,
              id: feature.name,
              value: dummyValueBasedOnType(feature.type ?? WorkflowInputFeature.type.TEXT),
            },
          }),
          {},
        )
        setLocalSchema(initialSchema)
      }
    },
  })

  const schema: Schema = schemaData?.data
    ? schemaData.data.reduce(
        (acc: Record<string, SchemaFeature>, feature: DeploymentSchemaFeatures) => ({
          ...acc,
          [feature.name]: {
            ...feature,
            id: feature.name,
            value: dummyValueBasedOnType(feature.type ?? WorkflowInputFeature.type.TEXT),
          },
        }),
        {},
      )
    : {}

  /*
   * Mutation hook for sending component test requests to the API
   */
  const sendComponentTestMutation = useMutation<
    AxiosResponse<ComponentTest>,
    AxiosError<{ detail: string }>,
    TestMutationParams,
    ComponentTest
  >({
    mutationFn: ({ projectId, componentId, body, componentType }) =>
      KonanAPI.testComponent({
        body: body,
        projectId: projectId,
        componentId: componentId,
        componentType: componentType,
      }),
    onSuccess: (data) => {
      if (data.data.status === ComponentTest.status.FAILED) {
        NotificationUtils.toast(t("testingFailedDesc"), {
          title: t("testingFailedTitle"),
          severity: "error",
        })
      }
    },
    onError: (error) => {
      NotificationUtils.toast(error.response?.data.detail as string, {
        title: t("testingError"),
        severity: "error",
      })
    },
  })

  /**
   * Switches between table and JSON editor modes
   * @param newEditor - The editor type to switch to
   */
  const handleEditorSwitch = (newEditor: EditorType): void => {
    if (newEditor === EDITOR_TYPES.JSON) {
      // Convert table data to JSON format
      const newData: Record<string, unknown> = {}
      // Use localSchema to get the current edited values
      const currentSchema = Object.keys(localSchema).length > 0 ? localSchema : schema
      for (const feature of Object.values(currentSchema)) {
        // falling back to null since JSON.stringify doesn't allow undefined
        // https://stackoverflow.com/a/32179927
        newData[feature.name] = typeCastBasedOnType(feature.value, feature.type) ?? null
      }

      setJson(JSON.stringify(newData, null, "\t"))
      setEditorType(newEditor)
    } else if (newEditor === EDITOR_TYPES.TABLE) {
      try {
        // Parse JSON and convert to table format while preserving nulls
        const parsedJson = convertNestedObjectsToFeatureStrings({
          obj: JSON.parse(json as string) ?? {},
          preserveNulls: true,
        })
        const newSchema = convertJsonToTableSchema(parsedJson, schemaData?.data)
        setIsJsonValid(true)
        setEditorType(newEditor)
        setLocalSchema(newSchema)
      } catch {
        setIsJsonValid(false)
      }
    }
  }

  /**
   * Builds the testing request based on the current editor type
   * @returns {Record<string, unknown>} The built testing request object
   * @throws Error if JSON is invalid
   */
  const buildTestingRequest = (): Record<string, unknown> => {
    if (editorType === EDITOR_TYPES.JSON) {
      return JSON.parse(json as string)
    }

    // Build request from table schema with respect to types
    const testingRequest: Record<string, unknown> = {}
    // Use localSchema for table mode to get the updated values
    const currentSchema = editorType === EDITOR_TYPES.TABLE ? localSchema : schema
    for (const item of Object.values(currentSchema)) {
      testingRequest[item.name] = typeCastBasedOnType(item.value, item.type)
    }
    return testingRequest
  }

  /**
   * Handles the submission of testing data to the API
   */
  const handleSendingTesting = async (): Promise<void> => {
    try {
      const backendTypeName = getApiComponentType(selectedComponent.type)
      const testingRequest = buildTestingRequest()
      await sendComponentTestMutation.mutateAsync({
        projectId: projectId as string,
        componentType: backendTypeName,
        body: testingRequest,
        componentId: selectedComponent.data.uuid as string,
      })
      setIsOutputExpanded(true)
    } catch (error) {
      setIsJsonValid(false)
      console.error("Failed to send testing:", error)
    }
  }

  /**
   * Adds a new feature to the schema with default values
   */
  const handleAddNewFeature = (): void => {
    const key = uuidv4()
    const feat = {
      [key]: {
        name: "",
        type: WorkflowInputFeature.type.TEXT,
        id: key,
        value: "string",
        new: true,
      },
    }

    setLocalSchema((prev: Schema) => {
      return {
        ...prev,
        ...feat,
      }
    })
  }

  const isTestingLoading = sendComponentTestMutation?.isLoading

  const testingData = sendComponentTestMutation?.data?.data

  return (
    <Grid container className={styles.root}>
      {/* Component Info */}
      <Grid item display={"flex"} gap={1}>
        <Grid item className={styles[`${getComponentDataFromType(selectedComponent.type)?.logoClassName}`]}>
          {(() => {
            const componentData = getComponentDataFromType(selectedComponent.type)
            const IconComponent = componentData?.Logo
            return IconComponent ? <IconComponent sx={{ color: theme.palette.base.text[1] }} /> : null
          })()}
        </Grid>
        <Grid item display={"flex"} flexDirection={"column"} justifyContent={"space-evenly"}>
          <Typography variant="label-bold">{selectedComponent.type.toUpperCase()}</Typography>
          <Typography variant="h3-bold" className={styles.componentName}>
            {selectedComponent.data.name}
          </Typography>
        </Grid>
      </Grid>

      <Grid container className={styles.editorContainer}>
        <Grid item display={"flex"} justifyContent={"space-between"}>
          <Grid item>
            <InputText
              variant="filled"
              placeholder={t("searchPlaceholder")}
              hideDescription
              disabled={editorType === EDITOR_TYPES.JSON || isSchemaLoading}
              value={search}
              type="text"
              handleChange={(e) => setSearch(e.target.value as string)}
            />
          </Grid>

          <Grid item display={"flex"} gap={1}>
            {editorType === EDITOR_TYPES.TABLE && (
              <Button variant="secondary" onClick={handleAddNewFeature} disabled={isSchemaLoading || isTestingLoading}>
                {t("buttons.addFeature")}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() =>
                handleEditorSwitch(editorType === EDITOR_TYPES.TABLE ? EDITOR_TYPES.JSON : EDITOR_TYPES.TABLE)
              }
              disabled={isSchemaLoading}
            >
              {(editorType === EDITOR_TYPES.TABLE ? EDITOR_TYPES.JSON : EDITOR_TYPES.TABLE).toUpperCase()}
            </Button>
            <Button
              variant="primary"
              onClick={handleSendingTesting}
              isLoading={isTestingLoading}
              disabled={isTestingLoading || isSchemaLoading}
            >
              {t("buttons.test")}
            </Button>
          </Grid>
        </Grid>

        <Grid item className={styles.editor} ref={tableRef} key={`output-${isOutputExpanded}-${rowsPerPage}`}>
          {isSchemaLoading ? (
            <Grid item display={"flex"} justifyContent={"center"} alignItems={"center"} height={"200px"}>
              <Typography variant="p">{t("loadingSchema")}</Typography>
            </Grid>
          ) : editorType === EDITOR_TYPES.JSON ? (
            // Adding key to make the editor re-render when the output expands to fix overflow
            <Fragment>
              <Grid item className={styles.jsonEditorHeader}>
                <Typography variant={"a"}>{t("jsonEditor.title")}</Typography>

                {!isJsonValid && (
                  <Typography variant={"a"} color="negative" variantColor={2}>
                    {t("jsonEditor.invalidDataError")}
                  </Typography>
                )}
              </Grid>

              <Grid item display={"flex"} flex={1} className={styles.monaco}>
                <MonacoTextEditor
                  value={convertNestedFeatureStringsToObjects(json ?? "{}")}
                  onChange={(value) => {
                    const nestedJsonObject = convertNestedFeatureStringsToObjects(value as string)
                    setJson(nestedJsonObject)
                  }}
                  height="100%"
                  language={"json"}
                  borderRadius={"0 0 8px 8px"}
                />
              </Grid>
            </Fragment>
          ) : (
            <TabularEditor
              rowsPerPage={rowsPerPage}
              schema={Object.keys(localSchema).length > 0 ? localSchema : schema}
              setSchema={setLocalSchema}
              page={page - 1}
              search={search}
            />
          )}
        </Grid>

        {editorType === EDITOR_TYPES.TABLE && (
          <Pagination
            page={page}
            onPageChange={setPage}
            pageSize={rowsPerPage}
            onPageSizeChange={(): void => {
              throw new Error("Function not implemented.")
            }}
            totalCount={Object.keys(Object.keys(localSchema).length > 0 ? localSchema : schema).length}
            variant={"complex"}
            hidePageSize
          />
        )}
      </Grid>

      {!!testingData && (
        <TestingOutput data={testingData} isExpanded={isOutputExpanded} setIsExpanded={setIsOutputExpanded} />
      )}
    </Grid>
  )
}
